const EMPTY_ROW_KEYS = [
  "day", "day_title", "entry_type", "time", "title",
  "activity_desc", "activity_lat", "activity_long",
  "hotel_name", "hotel_id", "hotel_options", "hotel_no_of_rooms",
  "transport_type", "transport_name", "transport_id",
];

function emptyRow(day) {
  const row = {};
  for (const k of EMPTY_ROW_KEYS) {
    row[k] = k === "day" ? day : "";
  }
  return row;
}

export function generateDaywiseCsvRows(
  noofdays,
  hotelSelections,
  transportationSelections,
  returnSelections,
  transferSelections
) {
  const rows = [];
  const lastDay = Number(noofdays);

  // Build lookup: day number -> hotel selection
  const hotelByDay = {};
  (hotelSelections || []).forEach((sel) => {
    (sel.day || []).forEach((d) => {
      hotelByDay[d.value] = sel;
    });
  });

  const onward = transportationSelections?.[0]?.selectedOnwardTransport;
  const ret = returnSelections?.[0]?.selectedReturnTransport;
  const transfer = transferSelections?.[0]?.selectedTransfers;

  for (let day = 1; day <= lastDay; day++) {
    if (day === 1 && onward) {
      rows.push({
        ...emptyRow(day),
        entry_type: "transport",
        transport_type: "onward",
        transport_name: onward.label || "",
        transport_id: onward.id ?? "",
      });
    }

    if (day === lastDay && ret) {
      rows.push({
        ...emptyRow(day),
        entry_type: "transport",
        transport_type: "return",
        transport_name: ret.label || "",
        transport_id: ret.id ?? "",
      });
    }

    if (transfer) {
      rows.push({
        ...emptyRow(day),
        entry_type: "transport",
        transport_type: "transfers",
        transport_name: transfer.label || "",
        transport_id: transfer.id ?? "",
      });
    }

    const hotelSel = hotelByDay[day];
    if (hotelSel) {
      rows.push({
        ...emptyRow(day),
        entry_type: "hotel",
        hotel_name: hotelSel.hotelName?.label || "",
        hotel_id: hotelSel.hotelName?.id ?? "",
        hotel_options: hotelSel.mealPlan?.label || "",
        hotel_no_of_rooms: hotelSel.noofRooms || "",
      });
    }

    // Blank starter rows for user to fill in
    rows.push({ ...emptyRow(day), entry_type: "activity" });
    rows.push({ ...emptyRow(day), entry_type: "meal" });
  }

  return rows;
}

const VALID_ENTRY_TYPES = ["activity", "hotel", "transport", "meal"];
const VALID_TRANSPORT_TYPES = ["onward", "return", "transfers"];

export function parseDaywiseCsvRows(
  rows,
  hotelNameOptions,
  onwardOptions,
  returnOptions,
  transferOptions
) {
  if (!rows || rows.length === 0) {
    return { days: [], errors: ["CSV is empty"] };
  }

  const headers = Object.keys(rows[0]);
  for (const col of ["day", "entry_type"]) {
    if (!headers.includes(col)) {
      return { days: [], errors: [`Missing required column: ${col}`] };
    }
  }

  const hotelById = {};
  (hotelNameOptions || []).forEach((h) => { hotelById[String(h.id)] = h; });

  const onwardById = {};
  (onwardOptions || []).forEach((t) => { onwardById[String(t.id)] = t; });

  const returnById = {};
  (returnOptions || []).forEach((t) => { returnById[String(t.id)] = t; });

  const transferById = {};
  (transferOptions || []).forEach((t) => { transferById[String(t.id)] = t; });

  const errors = [];
  const dayMap = {};

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const rowNum = i + 2;

    const dayNum = parseInt(row.day, 10);
    if (isNaN(dayNum) || dayNum < 1) {
      errors.push(`Row ${rowNum}: invalid day value "${row.day}"`);
      continue;
    }

    const entryType = (row.entry_type || "").trim().toLowerCase();
    if (!VALID_ENTRY_TYPES.includes(entryType)) {
      errors.push(`Row ${rowNum}: invalid entry_type "${row.entry_type}". Must be one of: activity, hotel, transport, meal`);
      continue;
    }

    if (!dayMap[dayNum]) {
      dayMap[dayNum] = { dayNumber: dayNum, planName: "", daywiseImage: null, timeSlots: [] };
    }

    if (row.day_title) {
      dayMap[dayNum].planName = row.day_title;
    }

    const time = row.time
      ? (/^\d{2}:\d{2}:\d{2}$/.test(row.time) ? row.time : `${row.time}:00`)
      : "00:00:00";
    const title = row.title || "";
    let slot = null;

    if (entryType === "activity") {
      slot = {
        entryType: "activity",
        time,
        title,
        description: row.activity_desc || "",
        lat: row.activity_lat ? parseFloat(row.activity_lat) : null,
        lng: row.activity_long ? parseFloat(row.activity_long) : null,
      };
    } else if (entryType === "hotel") {
      if (!row.hotel_id) {
        errors.push(`Row ${rowNum}: hotel row is missing hotel_id`);
        continue;
      }
      const hotelObj = hotelById[String(row.hotel_id)];
      if (!hotelObj) {
        errors.push(`Row ${rowNum}: hotel_id ${row.hotel_id} not found in available hotels`);
        continue;
      }
      slot = {
        entryType: "hotel",
        time,
        title,
        hotelType: hotelObj.hoteltypeid
          ? { hoteltypeid: hotelObj.hoteltypeid, hoteltype: hotelObj.hoteltype || "" }
          : null,
        hotelName: hotelObj,
        hotelOptions: row.hotel_options ? { label: row.hotel_options, value: row.hotel_options } : null,
        noofRooms: row.hotel_no_of_rooms || "",
      };
    } else if (entryType === "transport") {
      const ttype = (row.transport_type || "").trim().toLowerCase();
      if (!VALID_TRANSPORT_TYPES.includes(ttype)) {
        errors.push(`Row ${rowNum}: invalid transport_type "${row.transport_type}". Must be onward, return, or transfers`);
        continue;
      }
      if (!row.transport_id) {
        errors.push(`Row ${rowNum}: transport row is missing transport_id`);
        continue;
      }
      const tmap = ttype === "return" ? returnById : ttype === "transfers" ? transferById : onwardById;
      const tObj = tmap[String(row.transport_id)];
      if (!tObj) {
        errors.push(`Row ${rowNum}: transport_id ${row.transport_id} not found in ${ttype} transport options`);
        continue;
      }
      slot = {
        entryType: "transport",
        time,
        title,
        transportType: ttype,
        transportOption: tObj,
      };
    } else if (entryType === "meal") {
      slot = { entryType: "meal", time, title };
    }

    if (slot) {
      dayMap[dayNum].timeSlots.push(slot);
    }
  }

  if (errors.length > 0) {
    return { days: [], errors };
  }

  const days = Object.values(dayMap).sort((a, b) => a.dayNumber - b.dayNumber);
  return { days, errors: [] };
}
